// Header.tsx
import classNames from 'classnames';
import styles from './Header.module.scss';
import {
	Link,
	Logo,
	SkipToMain,
	Navigation,
	ServiceNavigation,
	ShoppingCart,
	Container,
	Grid,
	SearchField,
	Form,
	Button,
	Icon,
	LinkButton,
} from 'ui/components';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Avatar } from 'ui/components/3-organisms/Avatar';
import { useForm } from 'react-hook-form';
import { MouseEventHandler } from 'react';
import { LoginStatus } from 'ui/components/3-organisms/LoginStatus';
import { useSelector } from 'react-redux';
import { currency, subTotal } from 'application/adapters/store/slices/cartSlice';
import { LocaleContext } from 'application/adapters/context/LocaleContext';
import SearchIconWebsite from 'ui/icons/icon_search_website.svg';
import { throttle } from 'helpers/throttle';
import IconArrowRight from 'ui/icons/icon-arrow-right-web.svg';
import IconCloseCrossWebsite from 'ui/icons/icon_close_cross_website.svg';
import IconCloseWebsite from 'ui/icons/icon_close_website.svg';
import { RouteSpinner } from 'ui/components/2-molecules/RouteSpinner/RouteSpinner';
import { useRouteLoading } from 'helpers/useRouteLoading';

export interface HeaderProps {
	className?: string;
	navHeading?: string;
	contactLink?: Umbraco.Link;
	navigationItems?: Navigation.NestedNavigationItem[];
	serviceNavigationItems?: Navigation.NestedNavigationItem[];
	placeholder?: string;
	logoClicked: MouseEventHandler<HTMLAnchorElement>;
	href: string;
	handleSearchSubmit?: (query: string) => void;
	onSignIn: () => void;
	onSignOut: () => void;
	onNavigateToUserProfile?: () => void;
	profile?: Models.User;
	accessToken?: string;
	handleGoToCheckout: () => void;
	handleContactForm?: MouseEventHandler<HTMLAnchorElement>;
	contactFormLoading?: boolean;
	loginStatusDictionary?: { [key: string]: string };
	avatarDictionary?: { [key: string]: string };
	addresses?: Models.Address[];
	bpId?: string;
	links?: Models.NavigationItem[];
	dictionary?: { [key: string]: string };
	enableViewCart?: boolean;
	disabled?: boolean;
	continents?: Umbraco.Continent[];
}

export const Header: React.FC<HeaderProps> = ({
	className,
	navHeading,
	navigationItems,
	serviceNavigationItems,
	placeholder,
	logoClicked,
	href,
	profile,
	handleSearchSubmit,
	onSignIn,
	onSignOut,
	onNavigateToUserProfile,
	accessToken,
	handleGoToCheckout,
	handleContactForm,
	contactFormLoading,
	loginStatusDictionary,
	avatarDictionary,
	addresses,
	bpId,
	links,
	dictionary,
	enableViewCart = true,
	disabled,
	continents,
	contactLink,
}) => {
	const cartSubTotal = useSelector(subTotal);
	const [isNavActive, setIsNavActive] = useState(false);
	const [isAvatarActive, setIsAvatarActive] = useState(false);
	const currencyCode = useSelector(currency);
	const localeContext = useContext(LocaleContext);
	const [isActive, setIsActive] = useState<boolean>(true);
	const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
	const closeAvatar = () => setIsAvatarActive(false);

	useEffect(() => {
		if (isNavActive || isAvatarActive) setIsSearchOpen(false);
	}, [isNavActive, isAvatarActive]);

	useEffect(() => {
		const body = document.querySelector('body') as HTMLBodyElement | null;
		if (!body) return;
		if (isNavActive || isAvatarActive || isSearchOpen) {
			body.style.overflow = 'hidden';
		} else {
			body.style.overflow = '';
		}
	}, [isNavActive, isAvatarActive, isSearchOpen]);

	const prevScrollPosRef = useRef<number>(typeof window !== 'undefined' ? window.scrollY : 0);

	useEffect(() => {
		const onScroll = throttle(() => {
			if (isSearchOpen) return;

			const prevScrollPos = prevScrollPosRef.current;
			const currentScrollPos = window.scrollY;

			if (prevScrollPos <= 100 || prevScrollPos > currentScrollPos) {
				setIsActive(true);
			} else if (currentScrollPos > 100) {
				setIsActive(false);
			}
			prevScrollPosRef.current = currentScrollPos;
		}, 200);

		document.addEventListener('scroll', onScroll, { passive: true });
		return () => {
			document.removeEventListener('scroll', onScroll);
		};
	}, [isSearchOpen]);

	type FormData = {
		query: string;
		headerSearch: string;
	};

	const {
		handleSubmit,
		register,
		formState: { errors },
		setFocus,
	} = useForm<FormData>();

	useEffect(() => {
		if (!isSearchOpen) return;
		requestAnimationFrame(() => setFocus('headerSearch'));
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setIsSearchOpen(false);
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [isSearchOpen, setFocus]);

	const onSubmit = async ({ headerSearch }: { headerSearch: string }) => {
		handleSearchSubmit?.(headerSearch);
		setIsSearchOpen(false);
	};

	const closeSearch = () => setIsSearchOpen(false);

	// Close Nav when contact form is clicked/opened
	const openContactForm = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault();
		setIsNavActive(false);
		handleContactForm?.(e);
	};

	const routeLoading = useRouteLoading(250);
	return (
		<>
			<header className={classNames(styles.Header, { [styles.Header___hidden]: !isActive && !isSearchOpen }, className, placeholder)}>
				<SkipToMain link="#main" title="Click to skip navigations and go directly to content section">
					{dictionary?.skipToContent}
				</SkipToMain>

				<Container width="StandardWeb" className={styles.Header_topContainer}>
					<Grid className={styles.Header_top}>
						<section className={styles.Header_identity}>
							<Logo href={href} logoClicked={logoClicked} />
						</section>

						<div className={styles.Header_service}>
							<ServiceNavigation
								continents={continents}
								serviceNavigationItems={serviceNavigationItems || []}
								isLoggedIn={!!accessToken}
								dictionary={dictionary}
							/>

							<Avatar
								onSignIn={onSignIn}
								onSignOut={() => {
									setIsAvatarActive(false);
									onSignOut();
								}}
								disabled={disabled}
								profile={profile}
								isAvatarActive={isAvatarActive}
								onSetAvatarActive={() => setIsAvatarActive(!isAvatarActive)}
								dictionary={avatarDictionary}
							/>
							{contactLink && (
								<LinkButton
									style="tertiaryWeb"
									withBackIcon={<IconArrowRight />}
									className={styles.Header_contactCta}
									url={contactLink?.url}
									handleClick={(e) => {
										e.preventDefault();
										handleContactForm?.(e);
									}}
									disabled={contactFormLoading}
									showSpinner={contactFormLoading}
								>
									{contactLink.name}
								</LinkButton>
							)}
						</div>
					</Grid>

					{isAvatarActive && profile && (
						<LoginStatus
							onSignIn={onSignIn}
							onClose={closeAvatar}
							onSignOut={() => {
								setIsAvatarActive(false);
								onSignOut();
							}}
							onNavigateToUserProfile={() => {
								setIsAvatarActive(!isAvatarActive);
								onNavigateToUserProfile?.();
							}}
							handleLinkClick={() => setIsAvatarActive(!isAvatarActive)}
							profile={profile}
							dictionary={loginStatusDictionary}
							addresses={addresses}
							bpId={bpId}
							missingCustomerProfileMessage={
								loginStatusDictionary?.missingAddressesUrl &&
								loginStatusDictionary?.missingCustomerProfileMessage && (
									<Link
										url={loginStatusDictionary.missingAddressesUrl}
										handleClick={() => {
											setIsAvatarActive(false);
										}}
									>
										{loginStatusDictionary.missingCustomerProfileMessage}
									</Link>
								)
							}
							links={links}
						/>
					)}
				</Container>

				<div
					className={classNames(styles.Header_overlay, {
						[styles.isActive]: isAvatarActive,
					})}
					onClick={() => setIsAvatarActive(!isAvatarActive)}
				/>

				<Grid center={true} className={styles.Header_bottom}>
					<Container width="StandardWeb" className={styles.Header_bottomContainer}>
						{navigationItems && (
							<div
								className={classNames(styles.Header_navigation, {
									[styles.isActive]: isAvatarActive,
								})}
							>
								<div
									className={classNames(styles.Header_navToggle, {
										[styles.isActive]: isNavActive || isAvatarActive,
									})}
									onClick={() => {
										setIsNavActive(!isNavActive);
										if (isAvatarActive) {
											setIsNavActive(false);
											setIsAvatarActive(false);
										}
									}}
								>
									<span className={styles.Header_iconBar}></span>
									<span className={styles.Header_iconBar}></span>
									<span className={styles.Header_iconBar}></span>
								</div>

								{isNavActive && (
									<div className={styles.Header_mobileNav}>
										<div className={styles.Header_mobileAvatar}>
											<Avatar
												className={styles.Header_loginCta}
												onSignIn={onSignIn}
												onSignOut={onSignOut}
												profile={profile}
												isAvatarActive={isAvatarActive}
												onSetAvatarActive={() => setIsAvatarActive(!isAvatarActive)}
												dictionary={avatarDictionary}
											/>
											{contactLink && (
												<LinkButton
													style="tertiaryWeb"
													withBackIcon={<IconArrowRight />}
													className={styles.Header_contactCta}
													url={contactLink?.url}
													handleClick={(e) => openContactForm(e)}
												>
													{contactLink.name}
												</LinkButton>
											)}
										</div>

										<Navigation
											className={styles.Header_mobileGlobalNav}
											heading={navHeading}
											navigationItems={navigationItems}
											handleMobileNavLinkClick={() => {
												setIsNavActive(false);
											}}
										/>
										<ServiceNavigation
											serviceNavigationItems={serviceNavigationItems}
											continents={continents}
											isNavActive={isNavActive}
											isLoggedIn={!!accessToken}
											handleClick={() => {
												setIsNavActive(!isNavActive);
											}}
											dictionary={dictionary}
										/>
									</div>
								)}

								<div className={styles.Header_nav} data-ref="header__navPanel">
									<Grid nogutter={true} wrap={true}>
										<Navigation heading={navHeading} navigationItems={navigationItems} style="primary" />
									</Grid>
								</div>
							</div>
						)}
						<Button
							className={styles.Header_searchTrigger}
							aria-expanded={isSearchOpen}
							aria-controls="header-searchbar"
							onClick={() => {
								if (!isSearchOpen) {
									if (isAvatarActive) setIsAvatarActive(false);
									if (isNavActive) setIsNavActive(false);
									setIsActive(true);
									setIsSearchOpen(true);
								}
							}}
							title="Open search"
						>
							<Icon size="md" className={styles.ShoppingCart_icon}>
								<SearchIconWebsite />
							</Icon>
						</Button>

						<Logo href={href} logoClicked={logoClicked} className={styles.Header_identity___mobile} />

						<div className={styles.Header_cart}>
							<ShoppingCart
								bpId={bpId}
								accessToken={accessToken}
								handleGoToCheckout={handleGoToCheckout}
								formattedSum={localeContext.formatPrice(cartSubTotal, currencyCode)}
							/>
						</div>
					</Container>
				</Grid>

				<Form
					id="header-searchbar"
					role="search"
					aria-hidden={!isSearchOpen}
					onSubmit={handleSubmit(onSubmit)}
					className={classNames(styles.Header_identity___searchWrapper, {
						[styles.isActive]: isSearchOpen,
					})}
				>
					<Container width="StandardWeb">
						<div className={styles.Header_searchInputWrapper}>
							<SearchField
								placeholder={placeholder || 'Search'}
								name="headerSearch"
								state={errors.query ? { hasError: true, required: true } : { required: true }}
								register={register}
							/>

							<Button
								type="button"
								className={classNames(styles.Header_searchCloseButton, styles.onlyDesktop)}
								onClick={closeSearch}
								aria-label="Close search"
							>
								<Icon size="sm" className={classNames(styles.Header_searchCloseIcon)}>
									<IconCloseCrossWebsite />
								</Icon>
							</Button>
							<Button
								type="button"
								className={classNames(styles.Header_searchCloseButton, styles.onlyMobile)}
								onClick={closeSearch}
								aria-label="Close search"
							>
								<Icon size="sm" className={classNames(styles.Header_searchCloseIcon)}>
									<IconCloseWebsite />
								</Icon>
							</Button>
						</div>
					</Container>
				</Form>
			</header>
			<div
				className={classNames(styles.Header_lightbox, { [styles.isActive]: isSearchOpen })}
				onClick={closeSearch}
				aria-hidden={!isSearchOpen}
			/>
		</>
	);
};
